import React from "react";
import {
  Compass,
  ArrowLeft,
  Building2,
  CalendarDays,
  Newspaper,
  Info,
} from "lucide-react";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/components/Seo";

const NAVY = "#173A5E";
const BLUE = "#2E9CD6";
const LIGHT = "#6BC0E8";

const COPY = {
  nl: {
    seo: {
      title: "Pagina niet gevonden — Lemmer Leeft",
      description:
        "Deze pagina bestaat niet of is verhuisd. Ontdek Lemmer via de bedrijvengids, agenda, nieuws en praktische info.",
    },
    eyebrow: "Aangespoeld op een lege pagina",
    h1: "Deze plek staat niet op de kaart",
    body: "De pagina die je zocht bestaat niet of is verhuisd. Geen zorgen, in Lemmer valt genoeg te ontdekken. Zet koers naar één van deze plekken:",
    home: "Terug naar de homepage",
    links: {
      title: "Waar wil je heen?",
      bedrijvengids: { title: "Bedrijvengids", body: "Winkels, horeca, overnachten en meer." },
      agenda: { title: "Agenda", body: "Wat is er te doen in Lemmer?" },
      nieuws: { title: "Nieuws", body: "Verhalen en berichten uit Lemmer." },
      praktisch: { title: "Praktische info", body: "Bereikbaarheid, parkeren en voorzieningen." },
    },
  },
  de: {
    seo: {
      title: "Seite nicht gefunden — Lemmer Leeft",
      description:
        "Diese Seite existiert nicht oder wurde verschoben. Entdecken Sie Lemmer über das Unternehmensverzeichnis, die Veranstaltungen und praktische Informationen.",
    },
    eyebrow: "Auf einer leeren Seite gestrandet",
    h1: "Dieser Ort steht nicht auf der Karte",
    body: "Die gesuchte Seite existiert nicht oder wurde verschoben. Kein Problem, in Lemmer gibt es genug zu entdecken. Setzen Sie Kurs auf einen dieser Orte:",
    home: "Zurück zur Startseite",
    links: {
      title: "Wohin möchten Sie?",
      bedrijvengids: { title: "Unternehmensverzeichnis", body: "Geschäfte, Gastronomie, Übernachten und mehr." },
      agenda: { title: "Veranstaltungen", body: "Was ist los in Lemmer?" },
      nieuws: { title: "Nachrichten", body: "Geschichten und Berichte aus Lemmer." },
      praktisch: { title: "Praktische Infos", body: "Anreise, Parken und Einrichtungen." },
    },
  },
  en: {
    seo: {
      title: "Page not found — Lemmer Leeft",
      description:
        "This page doesn't exist or has moved. Discover Lemmer via the business directory, events, news and practical info.",
    },
    eyebrow: "Washed up on an empty page",
    h1: "This spot isn't on the map",
    body: "The page you were looking for doesn't exist or has moved. No worries — there's plenty to discover in Lemmer. Set course for one of these places:",
    home: "Back to the home page",
    links: {
      title: "Where would you like to go?",
      bedrijvengids: { title: "Business directory", body: "Shops, food & drink, places to stay and more." },
      agenda: { title: "Events", body: "What's on in Lemmer?" },
      nieuws: { title: "News", body: "Stories and updates from Lemmer." },
      praktisch: { title: "Practical info", body: "Getting here, parking and facilities." },
    },
  },
};

const LinkCard = ({ to, icon: Icon, title, body, testId }) => (
  <LLink
    to={to}
    data-testid={testId}
    className="group flex items-start gap-4 rounded-2xl border border-[#E4EEF5] bg-white p-5 shadow-sm hover:shadow-md hover:border-[#6BC0E8] transition-all"
  >
    <div className="w-11 h-11 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0 group-hover:bg-[#2E9CD6] group-hover:text-white transition-colors">
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <h3 className="font-[Poppins] font-bold text-[#173A5E] text-base mb-1">
        {title}
      </h3>
      <p className="text-[#3E5871] font-[Inter] text-sm leading-relaxed">
        {body}
      </p>
    </div>
  </LLink>
);

export const NotFoundPage = () => {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.nl;

  return (
    <div data-testid="not-found-page" className="bg-white">
      <Seo title={t.seo.title} description={t.seo.description} lang={lang} />

      {/* Hero */}
      <section className="relative overflow-hidden py-20 sm:py-28">
        {/* Zachte golf-achtergrond (Lemmer/haven tint) */}
        <div className="absolute inset-0 -z-10" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, #F4F8FB 0%, #E4EEF5 100%)",
            }}
          />
          <svg
            viewBox="0 0 1440 200"
            preserveAspectRatio="none"
            className="absolute bottom-0 left-0 w-full h-32 opacity-70"
          >
            <path
              d="M0,100 C240,160 480,40 720,100 C960,160 1200,40 1440,100 L1440,200 L0,200 Z"
              fill={LIGHT}
              opacity="0.35"
            />
            <path
              d="M0,130 C240,190 480,70 720,130 C960,190 1200,70 1440,130 L1440,200 L0,200 Z"
              fill={BLUE}
              opacity="0.25"
            />
          </svg>
        </div>

        <div className="ll-container max-w-3xl text-center">
          {/* Kompas illustratie */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center bg-white shadow-lg">
              <Compass
                className="w-10 h-10 sm:w-12 sm:h-12"
                style={{ color: NAVY }}
                strokeWidth={1.6}
              />
            </div>
          </div>

          <span
            data-testid="not-found-eyebrow"
            className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3"
          >
            {t.eyebrow}
          </span>
          <h1
            data-testid="not-found-h1"
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-[#173A5E] mb-4"
          >
            <span className="text-[#2E9CD6]">404</span> · {t.h1}
          </h1>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            {t.body}
          </p>
          <LLink
            to="/"
            data-testid="not-found-home-btn"
            className="ll-btn-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.home}
          </LLink>
        </div>
      </section>

      {/* Rijkere links */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-5xl">
          <p className="text-sm font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-6 text-center">
            {t.links.title}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <LinkCard
              to="/bedrijvengids"
              icon={Building2}
              title={t.links.bedrijvengids.title}
              body={t.links.bedrijvengids.body}
              testId="not-found-link-bedrijvengids"
            />
            <LinkCard
              to="/agenda"
              icon={CalendarDays}
              title={t.links.agenda.title}
              body={t.links.agenda.body}
              testId="not-found-link-agenda"
            />
            <LinkCard
              to="/nieuws"
              icon={Newspaper}
              title={t.links.nieuws.title}
              body={t.links.nieuws.body}
              testId="not-found-link-nieuws"
            />
            <LinkCard
              to="/praktisch"
              icon={Info}
              title={t.links.praktisch.title}
              body={t.links.praktisch.body}
              testId="not-found-link-praktisch"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFoundPage;
